import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CollapsedItemStates, computeIsItemExpanded} from '../../../../helpers/expansionTogglingHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../../../models/api/localPreferencesModel';
import {TeammateLicenseTypesEnum} from '../../../../models/api/teammateModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {VisualWorkspaceTeamData} from '../../../../models/api/visualWorkspaceModel';
import {AppState} from '../../../../store/appState';
import {getUserLicenseType} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeams} from '../../../../store/data/teams/teamsSelectors';
import {
  WorkspaceDropReorderTeamInboxHandler,
  WorkspaceDropReorderTeamViewHandler,
} from '../../../../store/data/workspace/reorder/types';
import {
  TagModelMap,
  TeamInboxModelMap,
} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {
  ToggleDropdownTeamCustomViewHandler,
  ToggleDropdownTeamInboxHandler,
  ToggleExpandItemHandler,
  ToggleTeamCustomViewHandler,
  ToggleTeamInboxHandler,
} from './context/types';
import {TeamWorkspaceEditor} from './teamWorkspaceEditor';

/*
 * Constants.
 */

const defaultIsTeamWorkspaceExpanded = true;

/*
 * Props.
 */

export interface TeamWorkspaceEditorContainerProps {
  /** Hash map of workspace item expansion toggle states. */
  collapsedItems: CollapsedItemStates;
  /** Handler for toggling the expanded/collapsed state of section items. */
  onToggleExpandSection: ToggleExpandItemHandler;
  /** Teams data passed from VisualWorkspaceModel */
  teamData: VisualWorkspaceTeamData;
  /** Whether the workspace is alphabetized. */
  isAlphabetized: boolean;
}

interface TeamWorkspaceEditorContainerComponentProps extends TeamWorkspaceEditorContainerProps {
  /** Team. */
  team: TeamModel | undefined;
  /** panel ID. */
  panelId: InboxesPanelId;
  /** User license type (full or light). */
  userLicenseType: TeammateLicenseTypesEnum;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpandClick: Handler;
  /** Event handler that manages a team inbox's checked state. */
  onToggleTeamInbox: ToggleTeamInboxHandler;
  /** Event handler that manages a team custom view's checked state. */
  onToggleTeamCustomView: ToggleTeamCustomViewHandler;
  onToggleDropdownTeamInbox: ToggleDropdownTeamInboxHandler;
  teamInboxModelMap: TeamInboxModelMap;
  teamCustomViewModelMap: TagModelMap;
  onToggleDropdownTeamCustomView: ToggleDropdownTeamCustomViewHandler;
  onDropReorderTeamInbox: WorkspaceDropReorderTeamInboxHandler;
  onDropReorderTeamView: WorkspaceDropReorderTeamViewHandler;
}

function buildTeamWorkspaceExpandedKey(teamId: number) {
  return `${CollapsedItemKeyPrefixesEnum.WORKSPACE}tim:${teamId}`;
}

/*
 * Selectors.
 */

function getTeamId(_state: AppState, props: TeamWorkspaceEditorContainerProps) {
  return props.teamData.teamId;
}

function makeGetTeam() {
  return createSelector([getTeams, getTeamId], (teams, id) => teams[id]);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeamWorkspaceEditorContainerProps>()(() => ({
  userLicenseType: getUserLicenseType,
  team: makeGetTeam(),
}));

const TeamWorkspaceEditorContainerComponent: FC<TeamWorkspaceEditorContainerComponentProps> = (props) => {
  const {team} = props;
  if (!team) {
    return null;
  }
  return <TeamWorkspaceEditor {...props} team={team} />;
};

export const TeamWorkspaceEditorContainer = connect(
  mapStateToProps,
  null,
  (stateProps, _dispatchProps, ownProps) => {
    const {onToggleExpandSection, collapsedItems, teamData} = ownProps;

    // Build the method to toggle the expanded state.
    const workspaceKey = buildTeamWorkspaceExpandedKey(teamData.teamId);
    const isExpanded = computeIsItemExpanded(collapsedItems, workspaceKey, defaultIsTeamWorkspaceExpanded);
    const onToggleExpandClick = () =>
      onToggleExpandSection({key: workspaceKey, defaultExpandedState: defaultIsTeamWorkspaceExpanded});

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick,
      isExpanded,
    };
  },
)(TeamWorkspaceEditorContainerComponent);
